import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Divider,
  Flex,
  FlexProps,
  Grid,
  HStack,
  Heading,
  HeadingProps,
  StackProps,
  Text,
  TextProps,
  chakra,
} from "@chakra-ui/react";
import {
  convertMinutesToHours,
  convertTimeTo12HourFormat,
} from "@picovico/utils";
import { format } from "date-fns";
import { BiCategory } from "react-icons/bi";
import { MdOutlineRateReview } from "react-icons/md";
import { TbTimelineEventExclamation } from "react-icons/tb";

export interface CartItems {
  idx: string;
  name: string;
  quantity: number;
  price: number;
  combo_package_items?: {
    idx: string;
    menu_item_idx: string;
    name: string;
  }[];
}

interface BookingInfo {
  price: number;
  seat: string;
  total_price: number;
  ticket_type: string;
}
interface GroupedBookingInfo {
  price_per_seat: number;
  seats: string[];
  total_price: number;
  ticket_type: string;
}
export interface BookingDetail {
  booking_info: BookingInfo[];
  show_info: {
    idx: string;
    cinema_hall: string;
    movie: string;
    grade: string;
    genre: string;
    duration: number;
    show_start_date_time: string;
  };
  three_d_charge: number;
}

export interface BookingSummaryProps {
  bookingSummaryContainerWrapperProps?: FlexProps;
  bookingSummaryContainerProps?: BoxProps;
  bookingSummaryContentBoxProps?: BoxProps;
  bookingSummaryHeadingProps?: HeadingProps;
  // Movie info box
  bookingSummaryMovieBoxProps?: BoxProps;
  bookingSummaryMovieLabelProps?: TextProps;
  bookingSummaryMovieTitleProps?: HeadingProps;
  bookingSummaryMetaRowProps?: StackProps;
  bookingSummaryInfoLabelProps?: TextProps;
  bookingSummaryInfoValueProps?: TextProps;
  // Ticket rows
  bookingSummaryTicketRowLabelProps?: TextProps;
  bookingSummaryTicketRowAmountProps?: TextProps;
  // Tax note + dividers
  bookingSummaryTaxNoteProps?: TextProps;
  bookingSummaryDividerProps?: BoxProps;
  // Sub-total
  bookingSummarySubtotalLabelProps?: TextProps;
  bookingSummarySubtotalAmountProps?: TextProps;
  // F&B section
  bookingSummaryFnbLabelProps?: TextProps;
  bookingSummaryFnbEmptyNoteProps?: TextProps;
  bookingSummaryFnbItemRowProps?: StackProps;
  bookingSummaryFnbItemNameProps?: TextProps;
  bookingSummaryFnbItemAmountProps?: TextProps;
  // Total bar + proceed
  bookingSummaryTotalBarProps?: StackProps;
  bookingSummaryTotalBarTextProps?: TextProps;
  bookingSummaryProceedButtonProps?: ButtonProps;
  // Decorative cutout circles
  bookingSummaryCutoutBg?: string;
}

function groupBookingInfo(bookingInfo: BookingInfo[]) {
  const grouped = new Map<string, GroupedBookingInfo>();

  for (const item of bookingInfo) {
    const key = `${item.ticket_type}-${item.price}`;

    if (grouped.has(key)) {
      const existing = grouped.get(key)!;
      existing.seats.push(item.seat);
      existing.total_price += item.total_price;
    } else {
      grouped.set(key, {
        ticket_type: item.ticket_type,
        price_per_seat: item.price,
        seats: [item.seat],
        total_price: item.total_price,
      });
    }
  }

  return Array.from(grouped.values());
}

function BookingSummary({
  items,
  bookingDetail,
  onProceed,
  isLoading,
  showIdx,
  movieIdx,
  bookingSummaryContainerWrapperProps,
  bookingSummaryContainerProps,
  bookingSummaryContentBoxProps,
  bookingSummaryHeadingProps,
  bookingSummaryMovieBoxProps,
  bookingSummaryMovieLabelProps,
  bookingSummaryMovieTitleProps,
  bookingSummaryMetaRowProps,
  bookingSummaryInfoLabelProps,
  bookingSummaryInfoValueProps,
  bookingSummaryTicketRowLabelProps,
  bookingSummaryTicketRowAmountProps,
  bookingSummaryTaxNoteProps,
  bookingSummaryDividerProps,
  bookingSummarySubtotalLabelProps,
  bookingSummarySubtotalAmountProps,
  bookingSummaryFnbLabelProps,
  bookingSummaryFnbEmptyNoteProps,
  bookingSummaryFnbItemRowProps,
  bookingSummaryFnbItemNameProps,
  bookingSummaryFnbItemAmountProps,
  bookingSummaryTotalBarProps,
  bookingSummaryTotalBarTextProps,
  bookingSummaryProceedButtonProps,
  bookingSummaryCutoutBg = "white",
}: {
  items: CartItems[];
  bookingDetail: BookingDetail;
  onProceed: () => void;
  isLoading: boolean;
  showIdx?: string;
  movieIdx?: string;
} & BookingSummaryProps) {
  const foodTotal = items?.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0,
  );

  const booking_info = bookingDetail?.booking_info || [];
  const subtotal = booking_info.reduce((sum, item) => sum + item.price, 0);
  const thee_d_charge =
    (bookingDetail?.three_d_charge || 0) * booking_info.length;

  const subtotalWith3DCharge = subtotal + thee_d_charge;
  const total = subtotalWith3DCharge + foodTotal;

  return (
    <Flex gap={4} direction={"column"} {...bookingSummaryContainerWrapperProps}>
      <Box
        bg="#F4F4F4"
        borderRadius="xl"
        h="fit-content"
        position="relative"
        {...bookingSummaryContainerProps}
      >
        {" "}
        <chakra.div
          position="absolute"
          top="50%"
          left="-1rem"
          w="2rem"
          h="2rem"
          bg={bookingSummaryCutoutBg}
          rounded="full"
          transform="translateY(-50%)"
        />
        <chakra.div
          position="absolute"
          top="50%"
          right="-1rem"
          w="2rem"
          h="2rem"
          bg={bookingSummaryCutoutBg}
          rounded="full"
          transform="translateY(-50%)"
        />
        <Box p={{ base: 4, md: 6 }} {...bookingSummaryContentBoxProps}>
          <Heading size="md" mb={4} {...bookingSummaryHeadingProps}>
            Booking Summary
          </Heading>
          {bookingDetail && (
            <>
              <Box
                bg="white"
                p={{ base: 3, md: 5 }}
                borderRadius="lg"
                border="1px"
                borderColor="gray.200"
                mb={4}
                {...bookingSummaryMovieBoxProps}
              >
                <Text
                  fontSize="xs"
                  color="gray.600"
                  mb={1}
                  {...bookingSummaryMovieLabelProps}
                >
                  Movie
                </Text>
                <Heading size="md" mb={3} {...bookingSummaryMovieTitleProps}>
                  {bookingDetail?.show_info?.movie}
                </Heading>

                <HStack
                  spacing={{ base: 2, md: 4 }}
                  fontSize="sm"
                  color="gray.600"
                  mb={4}
                  flexWrap="wrap"
                  {...bookingSummaryMetaRowProps}
                >
                  <HStack>
                    <TbTimelineEventExclamation />
                    <Text>
                      {convertMinutesToHours(
                        bookingDetail?.show_info.duration || 0,
                      )}
                    </Text>
                  </HStack>
                  <HStack>
                    <MdOutlineRateReview />
                    <Text>{bookingDetail?.show_info.grade}</Text>
                  </HStack>
                  <HStack>
                    <BiCategory />
                    <Text>{bookingDetail?.show_info.genre}</Text>
                  </HStack>
                </HStack>

                <Grid templateColumns="1fr 1fr" gap={2} fontSize="sm">
                  <Box>
                    <Text
                      color="gray.600"
                      fontSize="xs"
                      mb={1}
                      {...bookingSummaryInfoLabelProps}
                    >
                      Date
                    </Text>
                    <Text fontWeight="600" {...bookingSummaryInfoValueProps}>
                      {bookingDetail?.show_info.show_start_date_time &&
                        format(
                          new Date(
                            bookingDetail?.show_info.show_start_date_time,
                          ),
                          "EEE , do MMM",
                        )}
                    </Text>
                  </Box>
                  <Box>
                    <Text
                      color="gray.600"
                      fontSize="xs"
                      mb={1}
                      {...bookingSummaryInfoLabelProps}
                    >
                      Time
                    </Text>
                    <Text fontWeight="600" {...bookingSummaryInfoValueProps}>
                      {/* Cinema wall-clock time, read by position ("YYYY-MM-DD HH:mm…")
                          so the viewer's timezone can't shift it. */}
                      {bookingDetail?.show_info.show_start_date_time &&
                        convertTimeTo12HourFormat(
                          bookingDetail.show_info.show_start_date_time.slice(
                            11,
                            16,
                          ),
                        )}
                    </Text>
                  </Box>
                </Grid>
              </Box>
              {groupBookingInfo(booking_info).map((bi) => (
                <HStack key={bi.ticket_type} justify="space-between" mb={2}>
                  <Text
                    fontSize="sm"
                    color="gray.600"
                    {...bookingSummaryTicketRowLabelProps}
                  >
                    {bi.ticket_type} - {bi.seats.length} × Rs.
                    {bi.price_per_seat}
                  </Text>
                  <Text
                    fontWeight="bold"
                    {...bookingSummaryTicketRowAmountProps}
                  >
                    Rs. {bi.total_price}
                  </Text>
                </HStack>
              ))}

              {bookingDetail?.three_d_charge > 0 && (
                <HStack justify="space-between" mb={2}>
                  <Text
                    fontSize="sm"
                    color="gray.600"
                    {...bookingSummaryTicketRowLabelProps}
                  >
                    3D - {booking_info.length} Tickets
                  </Text>
                  <Text {...bookingSummaryTicketRowAmountProps}>
                    Rs. {thee_d_charge}
                  </Text>
                </HStack>
              )}
              <Text
                fontSize="xs"
                color="gray.500"
                mb={4}
                {...bookingSummaryTaxNoteProps}
              >
                (Inclusive of Rs. 17% Entertainment Tax & 17% VAT)
              </Text>

              <Divider
                border="1px solid #E5E5E5"
                my={3}
                {...bookingSummaryDividerProps}
              />
              <HStack justify="space-between" mb={3}>
                <Text
                  fontSize="sm"
                  fontWeight="600"
                  {...bookingSummarySubtotalLabelProps}
                >
                  Sub-Total
                </Text>
                <Text fontWeight="600" {...bookingSummarySubtotalAmountProps}>
                  Rs. {subtotalWith3DCharge}
                </Text>
              </HStack>
            </>
          )}
          <Text
            fontSize="sm"
            fontWeight="600"
            mb={2}
            {...bookingSummaryFnbLabelProps}
          >
            Food & Beverages
          </Text>
          {items?.length === 0 && (
            <Text
              fontSize="xs"
              color="gray.500"
              mb={4}
              {...bookingSummaryFnbEmptyNoteProps}
            >
              Select food to view food & beverages
            </Text>
          )}
          {items?.map((item, idx) => (
            <HStack
              justify="space-between"
              fontSize="sm"
              color="gray.700"
              mb={2}
              key={idx}
              {...bookingSummaryFnbItemRowProps}
            >
              <Text {...bookingSummaryFnbItemNameProps}>
                {item.name} x {item.quantity}
              </Text>
              <Text {...bookingSummaryFnbItemAmountProps}>
                Rs. {item.price * item.quantity}
              </Text>
            </HStack>
          ))}
          <Divider
            border="1px solid #E5E5E5"
            mt={6}
            {...bookingSummaryDividerProps}
          />
        </Box>
        <HStack
          justify="space-between"
          border="1px solid #E2F4FF"
          backgroundColor={"#E2F4FF"}
          borderBottomRadius="lg"
          p={{ base: 3, md: 4 }}
          {...bookingSummaryTotalBarProps}
        >
          <Text
            fontSize={{ base: "sm", md: "lg" }}
            fontWeight="700"
            {...bookingSummaryTotalBarTextProps}
          >
            Total Amount Payable
          </Text>
          <Text
            fontSize={{ base: "sm", md: "lg" }}
            fontWeight="700"
            {...bookingSummaryTotalBarTextProps}
          >
            Rs. {total}
          </Text>
        </HStack>
      </Box>

      <Button
        colorScheme="blue"
        size="lg"
        w="full"
        borderRadius="lg"
        bg="blue.500"
        _hover={{ bg: "blue.600" }}
        sx={{
          rounded: "full",
        }}
        onClick={onProceed}
        isLoading={isLoading}
        isDisabled={!(showIdx && movieIdx) && !items.length}
        {...bookingSummaryProceedButtonProps}
      >
        Proceed
      </Button>
    </Flex>
  );
}

export default BookingSummary;
